import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRectangleList,
  faHeart,
  faDog,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { showcasedPupsFundDogs } from "../api/showcasedPupsFundDogs";
import { showcaseSponsors } from "../api/showcaseSponsors";
import { PupsFundDogModel } from "../types/pupsFundDogModel";
import DogScroller from "../components/DogScroller";
import SponsorScroller from "../components/SponsorScroller";
import DonationWidget from "../components/DonationWidget";

interface Sponsor {
  name: string;
  image: string;
}

// This is the /donate page — hosts The Pups Fund and Operations donation widgets.
const Donate: React.FC = () => {
  const [pupsFundDogs, setPupsFundDogs] = useState<PupsFundDogModel[]>([]);
  const [isLoadingDogs, setIsLoadingDogs] = useState<boolean>(false);
  const [dogsLoadError, setDogsLoadError] = useState<string>("");
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);

  const stats = [
    {
      value: "$15,000+",
      label:
        "donated to rescues that save at-risk dogs to support their medical care",
      valueClass: "text-blue-strong",
    },
    {
      value: "100%",
      label: "of Pups Fund donations go directly to rescue dogs",
      valueClass: "text-orange",
    },
    {
      value: "20+",
      label: "rescue partners supported",
      valueClass: "text-blue-strong",
    },
  ];

  const moreWaysToHelp = [
    {
      icon: faRectangleList,
      title: "Wishlist",
      description: "Support dogs in foster care by purchasing supplies",
      buttonLabel: "See wishlists",
      link: "/browse/wishlists",
    },
    {
      icon: faHeart,
      title: "Adopt",
      description:
        "Browse adoptable dogs & start the adoption process with our shelter & rescue partners",
      buttonLabel: "Find a pup",
      link: "/browse",
    },
    {
      icon: faDog,
      title: "Foster",
      description:
        "Provide a safe temporary home & help a dog while they wait for adoption",
      buttonLabel: "Become a foster",
      link: "/foster-interest",
    },
    {
      icon: faUsers,
      title: "Volunteer",
      description:
        "Are you a rescue, networker, or shelter volunteer? Become a Pup Profile Community Partner!",
      buttonLabel: "Join our team",
      link: "/register",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPupsFundDogs = async () => {
      setIsLoadingDogs(true);
      try {
        const response = await showcasedPupsFundDogs();

        let dogsArray: PupsFundDogModel[] = [];
        if (response && Array.isArray(response) && response.length > 0) {
          if (
            response[0] &&
            typeof response[0] === "object" &&
            response[0].dogs &&
            Array.isArray(response[0].dogs)
          ) {
            dogsArray = response[0].dogs;
          } else {
            dogsArray = response;
          }
        } else if (
          response &&
          typeof response === "object" &&
          response.dogs &&
          Array.isArray(response.dogs)
        ) {
          dogsArray = response.dogs;
        }

        const processedDogs = dogsArray.map((dog) => ({
          id: Number(dog.id),
          name: String(dog.name).toLowerCase(),
          image: String(dog.image),
        })) as PupsFundDogModel[];

        setPupsFundDogs(processedDogs);
      } catch (error) {
        console.error("Failed to fetch showcase dogs:", error);
        setDogsLoadError("Failed to load dogs. Please try again later.");
      } finally {
        setIsLoadingDogs(false);
      }
    };

    fetchPupsFundDogs();
  }, []);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await showcaseSponsors();
        const sponsorsList = response?.[0]?.sponsors ?? [];
        const mapped = sponsorsList.map((s: any) => ({
          name: s.sponsor.name,
          image: s.sponsor.logo,
        }));
        setSponsors(mapped);
      } catch (error) {
        console.error("Error fetching showcase sponsors:", error);
      }
    };

    fetchSponsors();
  }, []);

  return (
    <section className="font-poppins text-gray-dark md:mb-20">
      {/* Hero */}
      <section className="flex w-full flex-col items-center gap-10">
        <div className="flex w-full flex-col items-center px-6 pt-16 lg:px-16 lg:pt-28">
          <div className="flex w-full max-w-[900px] flex-col items-center gap-10">
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-col items-center gap-3">
                <span className="w-fit rounded bg-blue-pale px-2 py-1 text-center text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                  100% of donations go directly to dogs
                </span>
                <h1 className="text-center text-4xl font-extrabold text-black">
                  Help fund a dog&rsquo;s second chance
                </h1>
              </div>
              <p className="text-center text-xl font-normal text-gray-dark">
                Every dollar donated to The Pups Fund goes directly toward the
                care of rescue dogs. From vaccines and medications to veterinary
                exams and spay/neuter procedures, your support helps recently
                saved shelter dogs get the care they need to start their next
                chapter.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="#donate-widget"
                className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                Donate to The Pups Fund
              </a>
              <a
                href="#operations-widget"
                className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
              >
                Support Operations
              </a>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex w-full flex-col items-center justify-center gap-12 px-6 py-12 sm:flex-row sm:gap-[73px] lg:px-16">
          {stats.map((stat) => (
            <div
              key={stat.value}
              className="flex flex-1 flex-col items-center gap-2"
            >
              <p
                className={`text-5xl font-extrabold tracking-[0.96px] ${stat.valueClass}`}
              >
                {stat.value}
              </p>
              <p className="max-w-[234px] text-center text-base text-gray6">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* The Pups Fund donation widget */}
      <DonationWidget
        id="donate-widget"
        donationType="pups_fund"
        fundName="The Pups Fund"
        badge="the pups fund"
        heading="100% of your donation supports rescue dogs"
        description={
          <>
            Donations made through{" "}
            <span className="font-semibold">The Pups Fund</span> help cover
            medical care for recently saved shelter dogs. Operational expenses
            are funded separately, allowing every dollar donated here to go
            directly toward helping dogs in need.
          </>
        }
        tiers={[
          { value: 25, label: "Vaccinations for one pup" },
          { value: 50, label: "Medications for one pup" },
          { value: 100, label: "Vet exam for one pup" },
          { value: 250, label: "Spay/neuter for one pup" },
        ]}
        submitLabel="Continue"
      />

      {/* Pups we've helped */}
      <section className="mt-16 px-6 lg:px-16">
        <h2 className="mb-8 text-center text-2xl font-semibold text-blue-strong">
          Pups we've helped
        </h2>

        {isLoadingDogs && (
          <div className="flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue border-t-transparent"></div>
          </div>
        )}

        {dogsLoadError && (
          <div className="mx-auto max-w-2xl text-center text-red-500">
            {dogsLoadError}
          </div>
        )}

        {!isLoadingDogs && !dogsLoadError && pupsFundDogs.length === 0 && (
          <p className="text-center text-gray-dark">
            We are currently working on getting more dogs funded. Please check
            back soon!
          </p>
        )}

        {!isLoadingDogs && !dogsLoadError && pupsFundDogs.length > 0 && (
          <div className="relative mx-auto mt-6 max-w-full overflow-hidden md:max-w-[1300px]">
            <DogScroller dogs={pupsFundDogs} />
          </div>
        )}
      </section>

      {/* More ways to help */}
      <section className="flex w-full flex-col items-center gap-12 px-6 py-20 lg:px-16">
        <div className="flex flex-col items-center gap-3">
          <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
            More ways to help
          </span>
          <h2 className="text-center text-3xl font-bold text-blue-strong">
            There are many ways to make a difference
          </h2>
        </div>

        <div className="flex flex-wrap items-stretch justify-center gap-10">
          {moreWaysToHelp.map((card) => (
            <div
              key={card.title}
              className="flex w-[271px] max-w-full flex-col items-center gap-8 p-6"
            >
              <div className="flex flex-1 flex-col items-center gap-6">
                <FontAwesomeIcon
                  icon={card.icon}
                  className="text-[2.5rem] text-gray1"
                />
                <div className="flex flex-col items-center gap-3">
                  <h3 className="text-2xl font-semibold text-black">
                    {card.title}
                  </h3>
                  <p className="text-center text-base font-normal text-gray-dark">
                    {card.description}
                  </p>
                </div>
              </div>
              <Link
                to={card.link}
                className="flex h-12 w-[223px] max-w-full items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                {card.buttonLabel}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Operations donation widget */}
      <DonationWidget
        id="operations-widget"
        donationType="pup_profile_operations"
        fundName="Pup Profile Operations"
        badge="Support pup profile"
        heading="Help us keep our lights on!"
        description={
          <>
            Donations to{" "}
            <span className="font-semibold">Pup Profile Operations</span> help
            us maintain and grow the platform so we can continue connecting dogs
            with adopters, fosters, rescue partners, and supporters.
          </>
        }
        tiers={[
          { value: 10 },
          { value: 20 },
          { value: 50 },
          { value: 100 },
        ]}
        submitLabel="Donate"
        submitIcon={faHeart}
      />

      {/* Our Sponsors */}
      {sponsors.length > 0 && (
        <section className="mt-16 px-6 lg:px-16">
          <h2 className="mb-8 text-center text-2xl font-semibold text-blue-strong">
            Our Sponsors
          </h2>
          <div className="relative mx-auto mt-6 max-w-full overflow-hidden md:max-w-[1300px]">
            <SponsorScroller sponsors={sponsors} />
          </div>
        </section>
      )}
    </section>
  );
};

export default Donate;
